<template>
  <div>
    <div class="d-flex align-center">
      <label
        v-if="label"
        class="v-label v-label--active theme--light mr-3"
        style="font-size: 12px; white-space: nowrap"
        >{{ label }}</label
      >
      <div
        class="d-flex align-center"
        :class="{ 'date-input-separate__fields--value-error': valueError }"
      >
        <v-text-field
          v-model="yearInput"
          :label="yearLabel"
          :dense="dense"
          :outlined="outlined"
          :flat="flat"
          :error="yearError || hasAnyError"
          :error-messages="yearError ? [yearErrorMessage] : []"
          hide-details
          :disabled="disabled"
          :readonly="readonly"
          type="text"
          maxlength="4"
          style="width: 60px"
          class="mr-2"
          @blur="handleYearBlur"
          @input="onFieldInput(validateYear)"
        />
        <v-text-field
          v-model="monthInput"
          :label="monthLabel"
          :dense="dense"
          :outlined="outlined"
          :flat="flat"
          :error="monthError || hasAnyError"
          :error-messages="monthError ? [monthErrorMessage] : []"
          hide-details
          :disabled="disabled"
          :readonly="readonly"
          type="text"
          maxlength="2"
          style="width: 60px"
          class="mr-2"
          @blur="handleMonthBlur"
          @input="onFieldInput(validateMonth)"
        />
        <v-text-field
          v-model="dayInput"
          :label="dayLabel"
          :dense="dense"
          :outlined="outlined"
          :flat="flat"
          :error="dayError || hasAnyError"
          :error-messages="dayError ? [dayErrorMessage] : []"
          hide-details
          :disabled="disabled"
          :readonly="readonly"
          type="text"
          maxlength="2"
          style="width: 60px"
          @blur="handleDayBlur"
          @input="onFieldInput(validateDay)"
        />
      </div>
    </div>
    <div
      v-if="showErrorDetails"
      class="v-text-field__details"
      :class="errorDetailsClass"
      style="padding-left: 0"
    >
      <div class="v-messages theme--light error--text" role="alert">
        <div class="v-messages__wrapper">
          <div class="v-messages__message">{{ combinedErrorMessage }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DateInputSeparate',
  props: {
    value: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    defaultYear: {
      type: [String, Number],
      default: null,
    },
    defaultMonth: {
      type: [String, Number],
      default: null,
    },
    defaultDay: {
      type: [String, Number],
      default: null,
    },
    yearLabel: {
      type: String,
      default: '年',
    },
    monthLabel: {
      type: String,
      default: '月',
    },
    dayLabel: {
      type: String,
      default: '日',
    },
    dense: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    flat: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    /** Red input values only; does not style the main label or sub-field labels. */
    valueError: {
      type: Boolean,
      default: false,
    },
    /** Extra spacing above validation messages (Vuetify utility classes, e.g. `mt-3 pt-1`). */
    errorDetailsClass: {
      type: String,
      default: 'mt-1',
    },
    errorMessages: {
      type: [String, Array],
      default: () => [],
    },
    hideDetails: {
      type: [Boolean, String],
      default: 'auto',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      yearInput: '',
      monthInput: '',
      dayInput: '',
      yearError: false,
      monthError: false,
      dayError: false,
      yearErrorMessage: '',
      monthErrorMessage: '',
      dayErrorMessage: '',
      isInternalUpdate: false, // Flag to prevent watcher from overwriting user input
    };
  },
  computed: {
    hasAnyError() {
      return this.error;
    },
    showGlobalError() {
      if (this.hideDetails === true) return false;
      return this.error;
    },
    /**
     * Show error details section when there are any errors (external or internal)
     */
    showErrorDetails() {
      if (this.hideDetails === true) return false;
      return this.error || this.hasInternalErrors;
    },
    globalErrorMessage() {
      if (Array.isArray(this.errorMessages)) {
        return this.errorMessages[0] || '';
      }
      return this.errorMessages || '';
    },
    /**
     * Combine all error messages into a single inline string
     * If internal validation errors exist, hide globalErrorMessage
     */
    combinedErrorMessage() {
      const internalMessages = [
        this.yearErrorMessage,
        this.monthErrorMessage,
        this.dayErrorMessage,
      ].filter(msg => msg);

      // If internal errors exist, only show them (not global error)
      if (internalMessages.length > 0) {
        return internalMessages.join('、');
      }

      // Otherwise show global error message
      return this.globalErrorMessage;
    },
    /**
     * Check if any internal validation error exists
     */
    hasInternalErrors() {
      return this.yearError || this.monthError || this.dayError;
    },
    /**
     * Computed value based on current inputs
     * Returns formatted date string if all fields are valid, null otherwise
     */
    computedValue() {
      // Don't emit value if there are validation errors
      if (this.hasInternalErrors) {
        return null;
      }

      if (
        this.yearInput &&
        this.monthInput &&
        this.dayInput &&
        this.yearInput.length === 4 &&
        this.monthInput.length >= 1 &&
        this.monthInput.length <= 2 &&
        this.dayInput.length >= 1 &&
        this.dayInput.length <= 2
      ) {
        const paddedMonth = this.padZero(this.monthInput);
        const paddedDay = this.padZero(this.dayInput);
        return `${this.yearInput}-${paddedMonth}-${paddedDay}`;
      }
      return null;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        // Skip echo-back of our own emit; still apply parent-driven overrides (e.g. end date sync)
        if (this.isInternalUpdate) {
          this.isInternalUpdate = false;
          if (newVal === this.computedValue) {
            return;
          }
        }

        this.syncFromProp(newVal);
      },
    },
  },
  mounted() {
    // Initialize with defaults if no value provided
    if (!this.value) {
      this.applyDefaults();
    }
  },
  methods: {
    /**
     * Pad single digit with leading zero
     */
    padZero(value) {
      const str = String(value);
      return str.length < 2 ? '0' + str : str;
    },

    /**
     * Apply default values to inputs
     */
    applyDefaults() {
      if (this.defaultYear) {
        this.yearInput = String(this.defaultYear);
      }
      if (this.defaultMonth) {
        this.monthInput = this.padZero(this.defaultMonth);
      }
      if (this.defaultDay) {
        this.dayInput = this.padZero(this.defaultDay);
      }
    },

    /**
     * Apply a parent-driven value (bypasses isInternalUpdate guard).
     * Used when another field programmatically updates this date input.
     */
    syncFromParentValue(newVal) {
      this.isInternalUpdate = false;
      this.syncFromProp(newVal);
    },

    /**
     * Sync internal state from prop value
     */
    syncFromProp(newVal) {
      if (newVal) {
        // Parse YYYY-MM-DD format
        const parts = newVal.split('-');
        if (parts.length === 3) {
          this.yearInput = parts[0];
          this.monthInput = parts[1];
          this.dayInput = parts[2];
        }
      } else {
        // Value is null/empty - reset to defaults or empty
        this.yearInput = this.defaultYear ? String(this.defaultYear) : '';
        this.monthInput = this.defaultMonth ? this.padZero(this.defaultMonth) : '';
        this.dayInput = this.defaultDay ? this.padZero(this.defaultDay) : '';
      }
    },

    /**
     * Handle any field input change
     */
    onFieldInput(validateFn) {
      if (validateFn && typeof validateFn === 'function') {
        validateFn();
      }
      this.emitValue();
    },

    /**
     * Handle year field blur
     */
    handleYearBlur() {
      this.validateYear();
      this.emitValue();
    },

    /**
     * Handle month field blur
     */
    handleMonthBlur() {
      this.validateMonth(true); // Auto-pad on blur
      this.emitValue();
    },

    /**
     * Handle day field blur
     */
    handleDayBlur() {
      this.validateDay(true); // Auto-pad on blur
      this.emitValue();
    },

    /**
     * Validate year input
     */
    validateYear() {
      this.yearError = false;
      this.yearErrorMessage = '';

      if (!this.yearInput) {
        return true; // Allow empty
      }

      const str = this.yearInput;

      // Check if it's a valid number
      if (!/^(\d){1,4}$/.test(str)) {
        this.yearError = true;
        this.yearErrorMessage = '年份輸入有誤';
        return false;
      }

      // Check if year is reasonable (e.g., 1900-2100)
      const year = parseInt(str, 10);
      if (year < 1900 || year > 2100) {
        this.yearError = true;
        this.yearErrorMessage = '年份超出範圍';
        return false;
      }

      return true;
    },

    /**
     * Validate month input
     * @param {boolean} autoPad - whether to auto-pad single digit (on blur)
     */
    validateMonth(autoPad = false) {
      this.monthError = false;
      this.monthErrorMessage = '';

      if (!this.monthInput) {
        return true; // Allow empty
      }

      const str = this.monthInput;

      // Check if it's 1-2 digits
      if (!/^(\d){1,2}$/.test(str)) {
        this.monthError = true;
        this.monthErrorMessage = '月份輸入有誤';
        return false;
      }

      // Check if month is valid (1-12)
      const month = parseInt(str, 10);
      if (month < 1 || month > 12) {
        this.monthError = true;
        this.monthErrorMessage = '月份必須在1-12之間';
        return false;
      }

      // Auto-pad with zero if single digit (only on blur)
      if (autoPad && str.length < 2) {
        this.monthInput = '0' + str;
      }

      return true;
    },

    /**
     * Validate day input
     * @param {boolean} autoPad - whether to auto-pad single digit (on blur)
     */
    validateDay(autoPad = false) {
      this.dayError = false;
      this.dayErrorMessage = '';

      if (!this.dayInput) {
        return true; // Allow empty
      }

      const str = this.dayInput;

      // Check if it's 1-2 digits
      if (!/^(\d){1,2}$/.test(str)) {
        this.dayError = true;
        this.dayErrorMessage = '日期輸入有誤';
        return false;
      }

      // Check if day is valid (1-31)
      const day = parseInt(str, 10);
      if (day < 1 || day > 31) {
        this.dayError = true;
        this.dayErrorMessage = '日期必須在1-31之間';
        return false;
      }

      // Additional validation: check if day is valid for the given month
      if (this.yearInput && this.monthInput) {
        const year = parseInt(this.yearInput, 10);
        const month = parseInt(this.monthInput, 10);
        const daysInMonth = new Date(year, month, 0).getDate();

        if (day > daysInMonth) {
          this.dayError = true;
          this.dayErrorMessage = `${month}月最多${daysInMonth}天`;
          return false;
        }
      }

      // Auto-pad with zero if single digit (only on blur)
      if (autoPad && str.length < 2) {
        this.dayInput = '0' + str;
      }

      return true;
    },

    /**
     * Emit the current value to parent
     * Emits null if any field is missing, incomplete, or has validation errors
     */
    emitValue() {
      // Set flag to prevent watcher from overwriting our changes
      this.isInternalUpdate = true;
      this.$emit('input', this.computedValue);
    },

    /**
     * Public method to validate all fields
     * Can be called externally for form validation
     * @returns {boolean} true if all fields are valid or empty
     */
    validate() {
      const yearValid = this.validateYear();
      const monthValid = this.validateMonth();
      const dayValid = this.validateDay();
      return yearValid && monthValid && dayValid;
    },

    /**
     * Check if the date is complete and valid
     * @returns {boolean} true if date is complete and has no errors
     */
    isValid() {
      return this.computedValue !== null && !this.hasInternalErrors;
    },

    /**
     * Reset all validation errors
     */
    resetValidation() {
      this.yearError = false;
      this.monthError = false;
      this.dayError = false;
      this.yearErrorMessage = '';
      this.monthErrorMessage = '';
      this.dayErrorMessage = '';
    },
  },
};
</script>

<style scoped>
.date-input-separate__fields--value-error >>> .v-text-field input {
  color: var(--v-error-base, #ff5252) !important;
  caret-color: var(--v-error-base, #ff5252);
}
</style>
